import React from "react";
import { GET_ONE, Link } from "react-admin";
import { Card, CardContent, CardHeader } from "@material-ui/core";
import dataProviderFactory from "../dataProvider";
import moment from "moment";
import styled from "styled-components";
import {
  prop,
  map,
  curry,
  path,
  isNil,
  converge,
  length,
  unnest,
  sortBy,
  aperture,
  range,
  last,
  concat,
  mapAccum,
  when,
  complement,
  addIndex
} from "ramda";
import LinearProgress from "@material-ui/core/LinearProgress";

const StyledLink = styled(Link)`
  :link {
    color: #000000;
    text-decoration: underline;
  }
  :visited {
    color: #000000;
  }
  :hover {
    color: #000000;
  }
  :active {
    color: #000000;
  }
`;

const ColumnLeft = styled.div(props => ({
  display: "flex",
  flexDirection: "row",
  backgroundColor: props.color,
  border: "1px solid #ffffff",
  width: "11rem",
  padding: "4px"
}));

const Column = styled.div(props => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: props.color,
  border: "1px solid #ffffff",
  width: "25rem",
  padding: "4px"
}));

const WeekDay = styled.div({
  width: "6.5rem",
  padding: "4px"
});
const Date = styled.div({
  width: "7rem",
  padding: "4px"
});

const Row = styled.div({
  display: "flex"
});

class ItemHistory extends React.Component {
  async fetchData(id, type) {
    const dataProvider = await dataProviderFactory("rest");
    const response = await dataProvider(GET_ONE, `item_history/${type}`, {
      id
    });

    return response.data;
  }

  async componentDidMount() {
    const id = this.props.match.params.id;
    const type = this.props.match.params.type;
    await this.updateState(id, type);
  }

  updateState = async (id, type) => {
    const data = await this.fetchData(id, type);
    this.setState({ data });
  };

  sortedDayList = (start, end, nextEventDate) => {
    const add = (day, offset) => day.clone().add(offset, "days");

    if (end.isSame(nextEventDate, "day")) {
      return map(curry(add)(start), range(0, end.diff(start, "days")));
    }
    return map(curry(add)(start), range(0, end.diff(start, "days") + 1));
  };

  blockForHoldEvent = (hold, nextEventDate) => {
    const start = moment(hold.start_date);
    const end = moment(hold.end_date);

    const days = this.sortedDayList(start, end, nextEventDate);

    return map(
      day => (
        <Row id={hold.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#FF00CC"}>
            Hold: &nbsp;{" "}
            <StyledLink to={{ pathname: `/holds/${hold.id}` }}>
              {hold.id}
            </StyledLink>
          </ColumnLeft>
          <Column color={"#FF00CC"}>{hold.comment}</Column>
        </Row>
      ),
      days
    );
  };

  blockForTour = (tour, nextEventDate) => {
    const start = moment(tour.effective_start_date);
    const end = moment(tour.effective_end_date);
    let days = this.sortedDayList(start, end, nextEventDate);

    return addIndex(map)((day, i) => {
      return (
        <Row id={tour.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#66CCFF"}>
            Item for Tour:&nbsp; {this.buildLinkForTour(tour, tour.id)}
          </ColumnLeft>
          <Column color={"#66CCFF"}>
            {`Guide Bike - ${path(["tour_name", "tour_name_en"], tour)}`}
          </Column>
        </Row>
      );
    }, days);
  };

  buildLinkForTour = (tour, title) => {
    return (
      <StyledLink
        target={"_blank"}
        to={{
          pathname: `/tours/${tour.id}/7`
        }}
      >
        {title}
      </StyledLink>
    );
  };

  buildLinkTourRental = (rental, title) => {
    return (
      <StyledLink
        to={{
          pathname: `/${rental.type === 2 ? "rental_clients" : "tour_clients"
            }/${rental.type === 2 ? rental.id : rental.id}/show`
        }}
      >
        {title}
      </StyledLink>
    );
  };

  buildTitleTourRental = (rental, color) => {
    return (
      <ColumnLeft color={rental.type === 2 ? color : "#CC9999"}>
        {" "}
        {rental.type === 2 ? "Rental" : "Tour"}: &nbsp;{" "}
        {this.buildLinkTourRental(
          rental,
          rental.type === 2 ? rental.id : rental.booking.tour_id
        )}
      </ColumnLeft>
    );
  };

  blockForRental = (rental, nextEventDate) => {
    const start = moment(rental.date_1);
    const end = moment(rental.date_2);

    let days = this.sortedDayList(start, end, nextEventDate);

    return addIndex(map)((day, i) => {
      const color = (() => {
        if (
          (i === 0 && rental.time_1 === "18:00:00") ||
          (i === length(days) - 1 && rental.time_2 === "10:00:00")
        ) {
          return "#FFCC66";
        }
        return "red";
      })();

      const extraInfo = (() => {
        if (this.props.match.params.type === "vehicles") {
          if (i === 0) {
            return <Column>{rental.km_1} km</Column>;
          }
          if (i === length(days) - 1) {
            return <Column>{rental.km_2} km</Column>;
          }
        }
        return null;
      })();

      return (
        <Row id={rental.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          {this.buildTitleTourRental(rental, color)}
          <Column
            color={rental.type === 2 ? color : "#CC9999"}
          >{`${rental.booking.r_first_name} ${rental.booking.r_last_name}`}</Column>

          {extraInfo}
        </Row>
      );
    }, days);
  };

  blockForTransfer = (transfer, nextEventDate) => {
    const start = moment(transfer.transfer_date);
    const end = moment(transfer.setup_date);

    const days = this.sortedDayList(start, end, nextEventDate);

    return map(
      day => (
        <Row id={transfer.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#9966FF"}>
            Transf: &nbsp;{" "}
            <StyledLink to={{ pathname: `/transfers/${transfer.id}` }}>
              {transfer.id}
            </StyledLink>
          </ColumnLeft>
          <Column color={"#9966FF"}>{transfer.TransferItem.notes}</Column>
        </Row>
      ),
      days
    );
  };

  daysForEvent = ({ event, type }, nextEventDate) => {
    switch (type) {
      case "hold":
        return this.blockForHoldEvent(event, nextEventDate);
      case "transfer":
        return this.blockForTransfer(event, nextEventDate);
      case "rental":
        return this.blockForRental(event, nextEventDate);
      case "tour":
        return this.blockForTour(event, nextEventDate);
      default:
        return this.blockForRental(event, nextEventDate);
    }
  };

  locationForEvent = ({ event, type }) => {
    switch (type) {
      case "hold":
        return null;
      case "transfer":
        return event.location_2;
      case "rental":
        return event.location_2;
      case "tour":
        return event.location_2;
      default:
        return event.location_2;
    }
  };

  buildTitle = itemType => {
    const titles = {
      gps_devices: "Gps Device",
      vehicles: "Vehicle",
      gloves: "Gloves",
      seats: "Seat",
      tankbags: "Tank Bag",
      jackets: "Jacket",
      helmets: "Helmet"
    };
    return `${titles[itemType]}`;
  };

  bikeAtBase = (prevEventDate, nextEventDate, location) => {
    if (nextEventDate === null) {
      return [];
    }

    const start = moment(prevEventDate).add(1, "day");
    const end = moment(nextEventDate).add(-1, "day");

    const days = this.sortedDayList(start, end);

    return map(
      curry((vehicle, day) => (
        <Row>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={location.color}>
            {vehicle.licence_plate} {vehicle.renting ? "®" : ""}
          </ColumnLeft>
          <Column color={location.color}>{location.location_name}</Column>
        </Row>
      ))(this.state.data),
      days
    );
  };

  render() {
    if (this.state === null) {
      return (
        <Card>
          <LinearProgress />
        </Card>
      );
    }

    const buildEvents = (events, type, start, end) =>
      map(event => ({ type, event, start, end }), events);

    const transfers = buildEvents(
      this.state.data.transfers,
      "transfer",
      "transfer_date",
      "setup_date"
    );
    const rentals = buildEvents(
      this.state.data.rentals,
      "rental",
      "date_1",
      "date_2"
    );

    const holds = buildEvents(
      this.state.data.holds,
      "hold",
      "start_date",
      "end_date"
    );

    const eventTypes = [transfers, rentals, holds];

    if (["gps_devices", "vehicles"].includes(this.props.match.params.type)) {
      const tours = buildEvents(
        this.state.data.tours,
        "tour",
        "effective_start_date",
        "effective_end_date"
      );
      eventTypes.push(tours);
    }

    const events = unnest(eventTypes);
    const startDate = converge(prop, [prop("start"), prop("event")]);
    const endDate = converge(prop, [prop("end"), prop("event")]);
    const sortedEvents = sortBy(startDate, events);

    const [location, eventsBlocks] = mapAccum(
      (location, [event, next]) => {
        const nextEventDate = when(complement(isNil))(startDate)(next);
        const lastLocation = this.locationForEvent(event) || location;

        return [
          lastLocation,
          concat(
            this.daysForEvent(event, nextEventDate),
            this.bikeAtBase(endDate(event), nextEventDate, lastLocation || {})
          )
        ];
      },
      null,
      concat(aperture(2, sortedEvents), [[last(sortedEvents), null]])
    );

    return (
      <Card>
        <CardHeader
          title={`History for ${this.buildTitle(
            this.props.match.params.type
          )} - ${this.state.data.model}`}
        />
        <CardContent>{eventsBlocks}</CardContent>
      </Card>
    );
  }
}

export default ItemHistory;



// WEBPACK FOOTER //
// ./src/components/itemHistory.js